import * as JsonDecoder from "ts.data.json";
import { DriverShiftStatus } from "../driver_shift_status";
import { DeliveryStatus } from "../delivery_status";

import type { IDispatchClient } from "src/admin/dispatch/dispatch_api_client";
import { Temporal } from "@js-temporal/polyfill";
import Modal from "src/admin/modal";
import { undefinedOrNullDecoder } from "src/common_decoders";
import { formatTime } from "./formatting";

type DriverGroupKey =
  | "declined"
  | "awaitingDeliveries"
  | "idle"
  | "busyPickingUp"
  | "busyDroppingOff"
  | "rostered"
  | "rosteredToStartSoon"
  | "droppedOut"
  | "notOnDashboard";

const compareByStartTime = (a: Driver, b: Driver): number => {
  // Strictly speaking we should always have a start time for these states but we have to check for typescript. If we get any they sort last.
  if (a.startTime === undefined) return b.startTime === undefined ? 0 : 1;
  if (b.startTime === undefined) return -1;
  return Temporal.ZonedDateTime.compare(a.startTime, b.startTime);
};

type DeliveryParams = Omit<Delivery, "driverNotAccepted" | "driverDeclined">;

class Delivery {
  id: string;
  orderId: number;
  href: string;
  storeId: number;
  storeHref: string;
  storeName: string;
  storePhone: string;
  storeAddress: string;
  name: string;
  bookingIdentifier: string | null;
  docketReference: string | null;
  phone: string;
  deliveryStatus: DeliveryStatus;
  readyToCollect: boolean | null;
  pickupSequence: number;
  pickupTime: string;
  pickedUpAt: string | null;
  collectedAt: Date | null;
  deliveryBusinessName: string | null;
  deliveryAddress: string;
  suburb: string;
  dropoffLatitude: number;
  dropoffLongitude: number;
  dropoffSequence: number;
  deliveryTime: string;
  deliveryInstructions: string | null;
  delaySent: number | null;
  dashNote: string;
  notifications: string[];
  requirements: string[]; // Delivery#dispatch_requirements
  driverId: number | null;
  groupId: number | null;
  operationsException: boolean;
  latestAssignmentTime: string | null;
  pickupTimeIso8601: Date;
  dropoffTimeIso8601: Date;
  makeNowFeature: boolean;
  allowGrouping: boolean;
  allowDeallocation: boolean;
  allowAllocation: boolean;
  allowPrepDelay: boolean;
  prepDelay: number;
  allowDelayMessage: boolean;
  originalPickupTime: string;
  originalDeliveryTime: string;
  deliveryMinutes: number;
  packageDescription: string[];
  declineReason: string | null;
  declineReasonDetails: string | null;

  constructor({
    id,
    orderId,
    href,
    storeId,
    storeHref,
    storeName,
    storePhone,
    storeAddress,
    name,
    bookingIdentifier,
    docketReference,
    phone,
    deliveryStatus,
    readyToCollect,
    pickupSequence,
    pickupTime,
    pickedUpAt,
    collectedAt,
    deliveryBusinessName,
    deliveryAddress,
    suburb,
    dropoffLatitude,
    dropoffLongitude,
    dropoffSequence,
    deliveryTime,
    deliveryInstructions,
    delaySent,
    dashNote,
    notifications,
    requirements,
    driverId,
    groupId,
    operationsException,
    latestAssignmentTime,
    pickupTimeIso8601,
    dropoffTimeIso8601,
    makeNowFeature,
    allowGrouping,
    allowDeallocation,
    allowAllocation,
    allowPrepDelay,
    prepDelay,
    allowDelayMessage,
    originalPickupTime,
    originalDeliveryTime,
    deliveryMinutes,
    packageDescription,
    declineReason,
    declineReasonDetails,
  }: DeliveryParams) {
    this.id = id;
    this.orderId = orderId;
    this.href = href;
    this.storeName = storeName;
    this.storePhone = storePhone;
    this.storeAddress = storeAddress;
    this.storeId = storeId;
    this.storeHref = storeHref;
    this.name = name;
    this.bookingIdentifier = bookingIdentifier;
    this.docketReference = docketReference;
    this.phone = phone;
    this.deliveryStatus = deliveryStatus;
    this.readyToCollect = readyToCollect;
    this.pickupSequence = pickupSequence;
    this.pickupTime = pickupTime;
    this.pickedUpAt = pickedUpAt;
    this.collectedAt = collectedAt;
    this.deliveryBusinessName = deliveryBusinessName;
    this.deliveryAddress = deliveryAddress;
    this.suburb = suburb;
    this.dropoffLatitude = dropoffLatitude;
    this.dropoffLongitude = dropoffLongitude;
    this.dropoffSequence = dropoffSequence;
    this.deliveryTime = deliveryTime;
    this.deliveryInstructions = deliveryInstructions;
    this.delaySent = delaySent;
    this.dashNote = dashNote;
    this.notifications = notifications;
    this.requirements = requirements;
    this.operationsException = operationsException;
    this.driverId = driverId;
    this.groupId = groupId;
    this.latestAssignmentTime = latestAssignmentTime;
    this.pickupTimeIso8601 = pickupTimeIso8601;
    this.dropoffTimeIso8601 = dropoffTimeIso8601;
    this.makeNowFeature = makeNowFeature;
    this.allowGrouping = allowGrouping;
    this.allowDeallocation = allowDeallocation;
    this.allowAllocation = allowAllocation;
    this.allowPrepDelay = allowPrepDelay;
    this.prepDelay = prepDelay;
    this.allowDelayMessage = allowDelayMessage;
    this.originalPickupTime = originalPickupTime;
    this.originalDeliveryTime = originalDeliveryTime;
    this.deliveryMinutes = deliveryMinutes;
    this.packageDescription = packageDescription;
    this.declineReason = declineReason;
    this.declineReasonDetails = declineReasonDetails;
  }

  driverNotAccepted(): boolean {
    return this.deliveryStatus == DeliveryStatus.Assigned || this.deliveryStatus == DeliveryStatus.DropoffReassigned;
  }

  driverDeclined(): boolean {
    return this.deliveryStatus == DeliveryStatus.DriverDeclined || this.deliveryStatus == DeliveryStatus.DropoffReassignedDeclined;
  }

  static decoder = JsonDecoder.object<DeliveryParams>({
    id: JsonDecoder.string(),
    orderId: JsonDecoder.number(),
    href: JsonDecoder.string(),
    storeId: JsonDecoder.number(),
    storeHref: JsonDecoder.string(),
    storeName: JsonDecoder.string(),
    storePhone: JsonDecoder.string(),
    storeAddress: JsonDecoder.string(),
    name: JsonDecoder.string(),
    bookingIdentifier: JsonDecoder.nullable(JsonDecoder.string()),
    docketReference: JsonDecoder.nullable(JsonDecoder.string()),
    phone: JsonDecoder.string(),
    deliveryStatus: JsonDecoder.enumeration<DeliveryStatus>(DeliveryStatus, 'DeliveryStatus'),
    readyToCollect: JsonDecoder.fallback(null, JsonDecoder.boolean()),
    pickupSequence: JsonDecoder.number(),
    pickupTime: JsonDecoder.string(),
    pickedUpAt: JsonDecoder.nullable(JsonDecoder.string()),
    collectedAt: JsonDecoder.nullable(JsonDecoder.string().map(stringDate => new Date(stringDate))),
    deliveryBusinessName: JsonDecoder.nullable(JsonDecoder.string()),
    deliveryAddress: JsonDecoder.string(),
    suburb: JsonDecoder.string(),
    dropoffLatitude: JsonDecoder.number(),
    dropoffLongitude: JsonDecoder.number(),
    dropoffSequence: JsonDecoder.number(),
    deliveryTime: JsonDecoder.string(),
    deliveryInstructions: JsonDecoder.nullable(JsonDecoder.string()),
    delaySent: JsonDecoder.nullable(JsonDecoder.number()),
    dashNote: JsonDecoder.fallback("", JsonDecoder.string()),
    notifications: JsonDecoder.array(JsonDecoder.string(), 'notifications[]'),
    requirements: JsonDecoder.array(JsonDecoder.string(), "requirements[]"),
    operationsException: JsonDecoder.boolean(),
    driverId: JsonDecoder.nullable(JsonDecoder.number()),
    groupId: JsonDecoder.nullable(JsonDecoder.number()),
    latestAssignmentTime: JsonDecoder.nullable(JsonDecoder.string()),
    pickupTimeIso8601: JsonDecoder.string().map(stringDate => new Date(stringDate)),
    dropoffTimeIso8601: JsonDecoder.string().map(stringDate => new Date(stringDate)),
    makeNowFeature: JsonDecoder.boolean(),
    allowGrouping: JsonDecoder.boolean(),
    allowDeallocation: JsonDecoder.boolean(),
    allowAllocation: JsonDecoder.boolean(),
    allowPrepDelay: JsonDecoder.boolean(),
    prepDelay: JsonDecoder.number(),
    allowDelayMessage: JsonDecoder.boolean(),
    originalPickupTime: JsonDecoder.string(),
    originalDeliveryTime: JsonDecoder.string(),
    deliveryMinutes: JsonDecoder.number(),
    packageDescription: JsonDecoder.array(JsonDecoder.string(), "PackageDescription"),
    declineReason: JsonDecoder.fallback(null, JsonDecoder.string()),
    declineReasonDetails: JsonDecoder.fallback(null, JsonDecoder.string()),
  }, "Delivery").map(params => new Delivery(params));
}

const PlainTimeDecoder: JsonDecoder.Decoder<Temporal.PlainTime> = JsonDecoder.string().map((str) => {
  return Temporal.PlainTime.from(str, { overflow: 'reject' });
});

const ZonedDateTimeDecoder: JsonDecoder.Decoder<Temporal.ZonedDateTime> = JsonDecoder.string().map((str) => {
  return Temporal.Instant.from(str).toZonedDateTimeISO("Pacific/Auckland");
});

enum DeliveryGroupStatus {
  Draft = "draft",
  ReadyToAssign = "ready_to_assign",
}

class DeliveryGroup {
  groupNumber: number;
  id: number;
  locked: boolean;
  selectedDriverId: number | null;
  status: DeliveryGroupStatus;

  constructor(groupNumber: number, id: number, locked: boolean, selectedDriverId: number | null, status: DeliveryGroupStatus) {
    this.groupNumber = groupNumber;
    this.id = id;
    this.locked = locked;
    this.selectedDriverId = selectedDriverId;
    this.status = status;
  }

  static decoder = JsonDecoder.object<DeliveryGroup>({
    groupNumber: JsonDecoder.number(),
    id: JsonDecoder.number(),
    locked: JsonDecoder.boolean(),
    // Both fall back so a new dash decodes an older backend's payload mid-rollout
    selectedDriverId: JsonDecoder.fallback(null, JsonDecoder.nullable(JsonDecoder.number())),
    status: JsonDecoder.fallback(DeliveryGroupStatus.Draft, JsonDecoder.enumeration<DeliveryGroupStatus>(DeliveryGroupStatus, "DeliveryGroupStatus")),
  }, "DeliveryGroup");
}

class DriverShiftTimes {
  startTime: Temporal.PlainTime;
  endTime: Temporal.PlainTime;

  constructor(startTime: Temporal.PlainTime, endTime: Temporal.PlainTime) {
    this.startTime = startTime;
    this.endTime = endTime;
  }

  static decoder = JsonDecoder.object<DriverShiftTimes>({
    startTime: PlainTimeDecoder,
    endTime: PlainTimeDecoder
  }, "DriverShiftTimes");
}


enum DropoutReason {
  Dropout = "dropout",
  NoComms = "no_comms",
}

enum WorkStatus {
  Pending = "Pending",
  Started = "Started",
  Working = "Working",
  Finished = "Finished",
  Dropout = "Dropout",
  NoComms = "NoComms",
}

// Matches DispatchDashboardSerializer.driver_info_hash
interface DriverInfo {
  id: number;
  areaId: number;
  status: DriverShiftStatus;
  name: string;
  mobileNumber: string;
  vehicleType: string;
  tenPmCurfew: boolean;
  rating: number | null;
  alcoholDeliveryAllowed: boolean;
  treatAsInexperienced: boolean;
  internalNote: string;
}

class Driver implements DriverInfo {
  id: number;
  areaId: number;
  status: DriverShiftStatus;
  pubnubUuid: string;
  name: string;
  mobileNumber: string;
  vehicleType: string;
  tenPmCurfew: boolean;
  rating: number | null;
  shiftId: number | null;
  dashNote: string;
  startTime?: Temporal.ZonedDateTime;
  endTime?: Temporal.ZonedDateTime;
  upcomingShiftTimes: DriverShiftTimes[];
  alcoholDeliveryAllowed: boolean;
  internalNote: string;
  requestedDeliveriesAt: string | null;
  requestedDeliveriesLocation: string;
  treatAsInexperienced: boolean;
  restrictedStoreIds: number[];
  workStatus?: WorkStatus;
  capabilities: string[]; // Driver#dispatch_capabilities

  constructor(
    id: number,
    areaId: number,
    status: DriverShiftStatus,
    pubnubUuid: string,
    name: string,
    mobileNumber: string,
    vehicleType: string,
    tenPmCurfew: boolean,
    rating: number | null,
    shiftId: number | null,
    dashNote: string,
    startTime: Temporal.ZonedDateTime,
    endTime: Temporal.ZonedDateTime,
    upcomingShiftTimes: DriverShiftTimes[],
    alcoholDeliveryAllowed: boolean,
    internalNote: string,
    requestedDeliveriesAt: string | null,
    requestedDeliveriesLocation: string | null,
    treatAsInexperienced: boolean,
    restrictedStoreIds: number[],
    workStatus?: WorkStatus,
    capabilities: string[] = [],
  ) {
    this.id = id;
    this.areaId = areaId;
    this.status = status;
    this.pubnubUuid = pubnubUuid;
    this.name = name;
    this.mobileNumber = mobileNumber;
    this.vehicleType = vehicleType;
    this.tenPmCurfew = tenPmCurfew;
    this.rating = rating;
    this.shiftId = shiftId;
    this.dashNote = dashNote;
    this.startTime = startTime;
    this.endTime = endTime;
    this.upcomingShiftTimes = upcomingShiftTimes;
    this.alcoholDeliveryAllowed = alcoholDeliveryAllowed;
    this.internalNote = internalNote;
    this.requestedDeliveriesAt = requestedDeliveriesAt;
    this.requestedDeliveriesLocation = requestedDeliveriesLocation || "";
    this.treatAsInexperienced = treatAsInexperienced;
    this.restrictedStoreIds = restrictedStoreIds;
    this.workStatus = workStatus;
    this.capabilities = capabilities;
  }

  static decoder = JsonDecoder.object<Driver>({
    id: JsonDecoder.number(),
    areaId: JsonDecoder.number(),
    status: JsonDecoder.enumeration<DriverShiftStatus>(DriverShiftStatus, 'DriverShiftStatus'),
    pubnubUuid: JsonDecoder.string(),
    name: JsonDecoder.string(),
    mobileNumber: JsonDecoder.string(),
    vehicleType: JsonDecoder.string(),
    tenPmCurfew: JsonDecoder.boolean(),
    rating: JsonDecoder.nullable(JsonDecoder.number()),
    shiftId: JsonDecoder.nullable(JsonDecoder.number()),
    dashNote: JsonDecoder.string(),
    startTime: undefinedOrNullDecoder(ZonedDateTimeDecoder),
    endTime: undefinedOrNullDecoder(ZonedDateTimeDecoder),
    upcomingShiftTimes: JsonDecoder.array<DriverShiftTimes>(DriverShiftTimes.decoder, "upcomingShiftTimes[]"),
    alcoholDeliveryAllowed: JsonDecoder.boolean(),
    internalNote: JsonDecoder.fallback("", JsonDecoder.string()),
    requestedDeliveriesAt: JsonDecoder.nullable(JsonDecoder.string()),
    requestedDeliveriesLocation: JsonDecoder.fallback("", JsonDecoder.string()),
    treatAsInexperienced: JsonDecoder.boolean(),
    restrictedStoreIds: JsonDecoder.array(JsonDecoder.number(), "restrictedStoreIds[]"),
    workStatus: JsonDecoder.fallback(undefined, JsonDecoder.enumeration<WorkStatus>(WorkStatus, 'WorkStatus')),
    capabilities: JsonDecoder.array(JsonDecoder.string(), "capabilities[]"),
  }, "Driver");
}

class TimeSlot {
  time: string;
  unformattedTime: string;
  rating: string;
  colour: string;
  panicked: boolean;
  isOperatingTime: boolean;

  constructor(t: string, unformattedTime: string, rating: string, colour: string, panicked: boolean, isOperatingTime: boolean) {
    this.time = t;
    this.unformattedTime = unformattedTime;
    this.rating = rating;
    this.colour = colour;
    this.panicked = panicked;
    this.isOperatingTime = isOperatingTime;
  }
}

class DispatchTask {
  id: number;
  deliveryId: string;
  taskType: string;
  status: string;
  createdAt: Temporal.ZonedDateTime;
  readyAt: Temporal.ZonedDateTime;

  constructor(id: number, deliveryId: string, taskType: string, status: string, createdAt: Temporal.ZonedDateTime, readyAt: Temporal.ZonedDateTime) {
    this.id = id;
    this.deliveryId = deliveryId;
    this.taskType = taskType;
    this.status = status;
    this.createdAt = createdAt;
    this.readyAt = readyAt;
  }

  static decoder = JsonDecoder.object<DispatchTask>({
    id: JsonDecoder.number(),
    deliveryId: JsonDecoder.string(),
    taskType: JsonDecoder.string(),
    status: JsonDecoder.string(),
    createdAt: ZonedDateTimeDecoder,
    readyAt: ZonedDateTimeDecoder,
  }, "DispatchTask");
}

type DashboardParams = {
  areaId: number;
  areaSlug: string;
  deliveries: Delivery[];
  drivers: Driver[];
  groups: DeliveryGroup[];
  tasks: DispatchTask[] | null;
  areaOpen: Date;
  areaClosed: Date;
  dispatcherId: number | null;
  dispatcherName: string | null;
  nextAvailablePickupTime: Date;
  tuning: number;
  autogroupingStatus: string;
  autogroupingControlEnabled: boolean;
  shiftTimes: Date[];
  features: string[];
}

class Dashboard {
  readonly areaId: number;
  readonly areaSlug: string;
  readonly deliveries: Delivery[];
  readonly areaOpen: Date;
  readonly areaClosed: Date;
  readonly dispatcherId: number | null;
  readonly dispatcherName: string | null;
  readonly nextAvailablePickupTime: Date;
  readonly tuning: number;
  readonly drivers: Driver[];
  readonly groups: DeliveryGroup[];
  readonly tasks: DispatchTask[] | null; // null when feature-flagged off
  readonly autogroupingStatus: string;
  readonly autogroupingControlEnabled: boolean;
  readonly shiftTimes: Date[]; // the whole shift day; filter against the current time before offering any
  readonly features: string[];

  private _driversActive?: Driver[];
  private _driversDeclined?: Driver[];
  private _driversNotAccepted?: Driver[];
  private _driversRostered?: Driver[];
  private _driversRosteredToStartSoon?: Driver[];
  private _driversDroppedOut?: Driver[];
  private _driversBusyPickingUp?: Driver[];
  private _driversBusyDroppingOff?: Driver[];
  private _driversAwaitingDeliveries?: Driver[];
  private _driversIdle?: Driver[];
  private _driversByGroup?: Map<DriverGroupKey, Driver[]>;
  private _unallocatedCurrent?: Delivery[];
  private _unallocatedFuture?: Delivery[];
  private _deliveriesById?: Map<string, Delivery>;
  private _driversById?: Map<number, Driver>;
  private _deliveriesByDriver?: Map<number, Delivery[]>;
  private _deliveriesByGroup?: Map<number, Delivery[]>;
  private _tasksByType?: Map<string, DispatchTask[]>;

  constructor({ areaId, areaSlug, deliveries, drivers, groups, areaOpen, areaClosed, dispatcherId, dispatcherName, nextAvailablePickupTime, tuning, tasks, autogroupingStatus, autogroupingControlEnabled, shiftTimes, features }: DashboardParams) {
    this.areaId = areaId;
    this.areaSlug = areaSlug;
    this.deliveries = deliveries;
    this.drivers = drivers;
    this.groups = groups;
    this.areaOpen = areaOpen;
    this.areaClosed = areaClosed;
    if (this.areaClosed <= this.areaOpen) {
      this.areaClosed.setDate(this.areaClosed.getDate() + 1);
    }
    this.dispatcherId = dispatcherId;
    this.dispatcherName = dispatcherName;
    this.nextAvailablePickupTime = nextAvailablePickupTime;
    this.tuning = tuning;
    this.tasks = tasks;
    this.autogroupingStatus = autogroupingStatus;
    this.autogroupingControlEnabled = autogroupingControlEnabled;
    this.shiftTimes = shiftTimes;
    this.features = features;
  }

  static emptyDashboard() {
    const areaOpen = new Date();
    const areaClosed = new Date(areaOpen);
    areaOpen.setHours(0, 0, 0)
    areaClosed.setHours(24, 0, 0)
    return new Dashboard({ areaId: 0, areaSlug: "", deliveries: [], drivers: [], groups: [], areaOpen, areaClosed, dispatcherId: null, dispatcherName: null, nextAvailablePickupTime: new Date(), tuning: 100, tasks: null, autogroupingStatus: 'hidden', autogroupingControlEnabled: false, shiftTimes: [], features: [] })
  }

  withChanges(args: Partial<DashboardParams>) {
    return new Dashboard(
      { ...this, ...args }
    )
  }

  driversActive(): Driver[] {
    return this._driversActive ??=
      this.drivers.filter(d => d.status == DriverShiftStatus.Active);
  }

  private classifyDriver(driver: Driver): DriverGroupKey {
    switch (driver.status) {
      case DriverShiftStatus.Active: {
        const deliveries = this.deliveriesForDriver(driver.id);
        if (deliveries.some(o => o.deliveryStatus == DeliveryStatus.DriverDeclined || o.deliveryStatus == DeliveryStatus.DropoffReassignedDeclined)) {
          return "declined";
        }
        if (deliveries.length === 0) {
          return driver.requestedDeliveriesAt !== null ? "awaitingDeliveries" : "idle";
        }
        return deliveries.some(o => o.pickedUpAt == null) ? "busyPickingUp" : "busyDroppingOff";
      }
      case DriverShiftStatus.RosteredButInactive:
        return "rostered";
      case DriverShiftStatus.RosteredToStartSoon:
        return "rosteredToStartSoon";
      case DriverShiftStatus.Dropout:
      case DriverShiftStatus.NoComms:
        return "droppedOut";
      case DriverShiftStatus.Inactive:
      case DriverShiftStatus.Unavailable:
      case DriverShiftStatus.Onboarding:
      case DriverShiftStatus.Offboarding:
      case DriverShiftStatus.Removed:
        return "notOnDashboard";
      default:
        return driver.status satisfies never;
    }
  }

  private driversByGroup(): Map<DriverGroupKey, Driver[]> {
    return this._driversByGroup ??= this.drivers.reduce((groups, driver) => {
      const key = this.classifyDriver(driver);
      const bucket = groups.get(key) ?? [];
      bucket.push(driver);
      groups.set(key, bucket);
      return groups;
    }, new Map<DriverGroupKey, Driver[]>());
  }

  driversDeclined(): Driver[] {
    return this._driversDeclined ??= this.driversByGroup().get("declined") ?? [];
  }

  driversNotAccepted(): Driver[] {
    return this._driversNotAccepted ??=
      this.drivers.filter(d => !this.driversDeclined().includes(d) && this.deliveriesForDriver(d.id).some(o => o.driverNotAccepted()));
  }

  driversRostered(): Driver[] {
    return this._driversRostered ??=
      [...(this.driversByGroup().get("rostered") ?? [])].sort(compareByStartTime);
  }

  driversRosteredToStartSoon(): Driver[] {
    return this._driversRosteredToStartSoon ??=
      [...(this.driversByGroup().get("rosteredToStartSoon") ?? [])].sort(compareByStartTime);
  }

  driversDroppedOut(): Driver[] {
    return this._driversDroppedOut ??= this.driversByGroup().get("droppedOut") ?? [];
  }

  driversBusyPickingUp(): Driver[] {
    return this._driversBusyPickingUp ??= this.driversByGroup().get("busyPickingUp") ?? [];
  }

  driversBusyDroppingOff(): Driver[] {
    return this._driversBusyDroppingOff ??= this.driversByGroup().get("busyDroppingOff") ?? [];
  }

  driversAwaitingDeliveries(): Driver[] {
    return this._driversAwaitingDeliveries ??=
      [...(this.driversByGroup().get("awaitingDeliveries") ?? [])].sort((a, b) =>
        (a.requestedDeliveriesAt || "").localeCompare(b.requestedDeliveriesAt || ""));
  }

  driversIdle(): Driver[] {
    return this._driversIdle ??= this.driversByGroup().get("idle") ?? [];
  }

  unallocatedCurrent(now: Date): Delivery[] {
    return this._unallocatedCurrent ??=
      this.deliveries.filter(o => o.driverId == null && o.groupId == null && this.isCurrentDelivery(o, now));
  }

  unallocatedFuture(now: Date): Delivery[] {
    return this._unallocatedFuture ??=
      this.deliveries.filter(o => o.driverId == null && o.groupId == null && !this.isCurrentDelivery(o, now));
  }

  deliveriesForDriver(driverId: number) : Delivery [] {
    return this.deliveriesByDriver().get(driverId) ?? [];
  }

  deliveriesForGroup(groupId: number) : Delivery[] {
    return this.deliveriesByGroup().get(groupId) ?? [];
  }

  deliveriesById(): Map<string, Delivery> {
    return this._deliveriesById ??=
      this.deliveries.reduce((results, delivery) => {
        results.set(delivery.id, delivery);
        return results;
      }, new Map<string, Delivery>());
  }

  driversById(): Map<number, Driver> {
    return this._driversById ??=
      this.drivers.reduce((results, driver) => {
        results.set(driver.id, driver);
        return results;
      }, new Map<number, Driver>());
  }

  deliveriesByDriver(): Map<number, Delivery[]> {
    return this._deliveriesByDriver ??=
      this.deliveries.reduce((results, delivery) => {
        if (delivery.driverId) {
          const deliveriesForDriver = results.get(delivery.driverId) ?? [];
          deliveriesForDriver.push(delivery);
          results.set(delivery.driverId, deliveriesForDriver)
        }
        return results;
      }, new Map<number, Delivery[]>());
  }

  deliveriesByGroup(): Map<number, Delivery[]> {
    return this._deliveriesByGroup ??=
      this.deliveries.reduce((results, delivery) => {
        if (delivery.groupId) {
          const deliveriesForGroup = results.get(delivery.groupId) ?? [];
          deliveriesForGroup.push(delivery);
          results.set(delivery.groupId, deliveriesForGroup)
        }
        return results;
      }, new Map<number, Delivery[]>());
  }

  tasksByType(): Map<string, DispatchTask[]> | undefined {
    return this._tasksByType ??=
      this.tasks?.reduce((results, task) => {
        const tasksForType = results.get(task.taskType) ?? [];
        tasksForType.push(task);
        results.set(task.taskType, tasksForType);
        return results;
      }, new Map<string, DispatchTask[]>());
  }

  availableDriversForDeliveries(deliveries: Delivery[]): Driver[] {
    const storeIds = deliveries.map(delivery => delivery.storeId);
    const enforced = this.enforcedRequirements();
    const requirements = deliveries.flatMap(delivery => delivery.requirements).filter(r => enforced.includes(r));

    return this.driversActive().filter(driver => {
      const anyDeliveryIsAllocatedToDriver = deliveries.some(delivery => delivery.driverId == driver.id);
      const anyDeliveryIsDriverRestricted = driver.restrictedStoreIds.some(id => storeIds.includes(id));

      if (anyDeliveryIsAllocatedToDriver || anyDeliveryIsDriverRestricted) {
        return false;
      }

      return requirements.every(requirement => driver.capabilities.includes(requirement));
    });
  }

  async handleAllocateDeliveryToDriver(deliveryId: string, driverId: number, apiClient: IDispatchClient): Promise<boolean> {
    const delivery = this.deliveries.find(d => d.id == deliveryId);

    if (delivery == undefined || !delivery.allowAllocation) return false;
    if (!this.availableDriversForDeliveries([delivery]).map(d => d.id).includes(driverId)) return false;
    if (this.isDriverAlreadyDelivering(driverId) && !await this.confirmAddMoreDeliveries()) return false;

    await apiClient.allocateDeliveryToDriver(deliveryId, driverId, delivery.driverId);
    return true;
  }

  async handleAllocateGroupToDriver(groupId: number, driverId: number, apiClient: IDispatchClient): Promise<boolean> {
    const deliveries = this.deliveriesForGroup(groupId);

    if (deliveries.length == 0 || deliveries.some(d => !d.allowAllocation)) return false;
    if (!this.availableDriversForDeliveries(deliveries).map(d => d.id).includes(driverId)) return false;
    if (this.isDriverAlreadyDelivering(driverId) && !await this.confirmAddMoreDeliveries()) return false;

    await apiClient.allocateGroupToDriver(groupId, driverId);
    return true;
  }

  async confirmAddMoreDeliveries() {
    return await Modal.show({
      title: "Are you sure?",
      content: "This driver has picked up all their current deliveries and is now delivering. Adding more deliveries to them will prevent them from being able to complete their current deliveries until they have picked up these new deliveries. You should only do this if you are expecting the driver to go pick up these deliveries <b>right now</b>",
      acceptButtonLabel: "Allocate deliveries",
    }).catch(() => undefined);
  }

  isDriverAlreadyDelivering(driverId: number): boolean {
    const existingDeliveriesOnDriver = this.deliveriesForDriver(driverId) ?? [];

    return (existingDeliveriesOnDriver.length > 0 && existingDeliveriesOnDriver.every(o => o.pickedUpAt !== null));
  }

  isDriverAssignedDeliveries(driverId: number): boolean {
    const existingDeliveriesOnDriver = this.deliveriesForDriver(driverId) ?? [];

    return existingDeliveriesOnDriver.length > 0;
  }

  hasFeature(feature: string): boolean {
    return this.features.includes(feature);
  }

  // Mirrors Delivery.dispatch_requirements_enforced_in
  enforcedRequirements(): string[] {
    return ["age_restricted", ...(this.hasFeature("enforce_gear_requirements") ? ["hot_bag_required", "pizza_bag_required"] : [])];
  }

  groupsSelectedForDriver(driverId: number): DeliveryGroup[] {
    return this.groups.filter(group => group.selectedDriverId === driverId);
  }

  heatMap(now : Date): TimeSlot[] {
    const timeSlots : TimeSlot[] = []
    const nearestSlot = new Date((Math.round(now.getTime() / 300000) + 1) * 300000);

    this.timeSlotsVisibleOnHeatmap(now).forEach((slotStart, index) => {
      const rating = this.timeSlotRating(index, now);
      const panicTime = new Date(Math.max(this.nextAvailablePickupTime.getTime(), nearestSlot.getTime()));
      timeSlots.push(new TimeSlot(
        formatTime(slotStart),
        slotStart.toISOString(),
        rating.toString().concat("%"),
        this.timeSlotColour(rating),
        panicTime > slotStart,
        (slotStart >= this.areaOpen && slotStart <= this.areaClosed) || index == 0
      ));
    });

    return timeSlots;
  } 

  private timeSlotRating(slotPosition: number, now: Date) {
    const firstSlot = Math.round(now.getTime() / 300000) * 300000;
    const slotStart = firstSlot + (5 * 60 * 1000 * slotPosition);
    const slotEnd = firstSlot + (5 * 60 * 1000 * (slotPosition+1));

    const estimatedDeliveryMinutes = this.deliveries.reduce((prev, delivery) => {
      const deliveryStart = delivery.collectedAt?.getTime() ?? Math.max(delivery.pickupTimeIso8601.getTime(), firstSlot);
      const deliveryEnd = deliveryStart + delivery.deliveryMinutes*60*1000;
      const overlapStart = Math.max(slotStart, deliveryStart);
      const overlapEnd = Math.min(slotEnd, deliveryEnd);
      const overlapMillis = Math.max(overlapEnd - overlapStart, 0);
      return prev + overlapMillis;
    }, 0)/60/1000;

    const optimalMinutes = this.optimalDeliveryMinutesPerTimeSlot()
    if (optimalMinutes == 0 || estimatedDeliveryMinutes == 0){
      return 0.0
    } else {
      return Math.round((estimatedDeliveryMinutes / optimalMinutes) * 100)
    }
  }

  private timeSlotsVisibleOnHeatmap(now : Date) : Date[]{
    const firstSlot = Math.round(now.getTime() / 300000) * 300000;
    const slots = [];
    for (let i = 0; i < 15; i++) {
      const slotStart = new Date(firstSlot + (5 * 60 * 1000 * i));
      slots.push(slotStart);
    }
    return slots;
  }

  private optimalDeliveryMinutesPerTimeSlot(): number{
    return (this.driversActive().length * 5.0) * (this.tuning / 100);
  }

  private timeSlotColour(rating: number) : string {
    if (rating >= 120) {
      return 'red-time-slot dash-time-slot'
    } else if (rating >= 105){
      return 'orange-time-slot dash-time-slot'
    } else if (rating >= 90){
      return 'yellow-time-slot dash-time-slot'
    } else if (rating >= 75){
      return 'bright-green-time-slot dash-time-slot'
    } else if (rating >= 60){
      return 'muted-green-time-slot dash-time-slot'
    } else if (rating >= 35){
      return 'grey-time-slot dash-time-slot'
    } else {
      return 'clear-time-slot dash-time-slot'
    }
  }

  private isCurrentDelivery(delivery : Delivery, now : Date) {
    const minutesTillAreaOpen = (this.areaOpen.getTime() - now.getTime())/60000;
    const minutesTillPickupTime = (delivery.pickupTimeIso8601.getTime() - now.getTime())/60000;
    return minutesTillAreaOpen <= 15 && minutesTillPickupTime <= 55;
  }

  static async decode(data: unknown) : Promise<Dashboard> {
    const dash = await Dashboard.decoder
      .decodePromise(data)

    return dash;
  }

  static decoder = JsonDecoder.object<DashboardParams>({
    areaId: JsonDecoder.number(),
    areaSlug: JsonDecoder.string(),
    deliveries: JsonDecoder.array(Delivery.decoder, "Delivery[]"),
    drivers: JsonDecoder.array(Driver.decoder, "Driver[]"),
    areaOpen: JsonDecoder.string().map(stringDate => new Date(stringDate)),
    areaClosed: JsonDecoder.string().map(stringDate => new Date(stringDate)),
    dispatcherId: JsonDecoder.nullable(JsonDecoder.number()),
    dispatcherName: JsonDecoder.nullable(JsonDecoder.string()),
    nextAvailablePickupTime: JsonDecoder.fallback(new Date(), JsonDecoder.string().map(stringDate => new Date(stringDate))),
    tuning: JsonDecoder.number(),
    groups: JsonDecoder.array(DeliveryGroup.decoder, "DeliveryGroup[]"),
    tasks: JsonDecoder.nullable(JsonDecoder.array(DispatchTask.decoder, "DispatchTask[]")),
    autogroupingStatus: JsonDecoder.string(),
    autogroupingControlEnabled: JsonDecoder.fallback(false, JsonDecoder.boolean()),
    shiftTimes: JsonDecoder.array(JsonDecoder.string().map(stringDate => new Date(stringDate)), "shiftTimes[]"),
    features: JsonDecoder.array(JsonDecoder.string(), "features[]"),
  }, "Dashboard").map(d => new Dashboard(d));
}

interface DeliveryTrackingStep {
  readonly seconds: number;
  readonly metres: number;
  readonly points: string;
}

interface DeliveryTracking {
  readonly uid: string;
  readonly liveArrivalEstimate?: Date;
  readonly liveLastUpdate: Date;
  readonly routeToDropoff?: DeliveryTrackingStep[];
  readonly routeToPickup?: DeliveryTrackingStep[];
}

interface DriverTrackingUpdate {
  driverId: number;
  latitude: number;
  longitude: number;
  receivedAt: Date;
  deliveryTrackingList: DeliveryTracking[];
}

interface VisibleStoreWindow {
  readonly start: Date;
  readonly end: Date;
}

interface VisibleStore {
  readonly storeId: number;
  readonly name: string;
  readonly href: string;
  readonly disabled: boolean;
  readonly closesAt: Date | null;
  readonly lastOrdersAt: Date | null;
  readonly pausedUntil: Date | null;
  readonly windowsToday: VisibleStoreWindow[];
}

const visibleStoreWindowDecoder = JsonDecoder.object<VisibleStoreWindow>({
  start: JsonDecoder.string().map(stringDate => new Date(stringDate)),
  end: JsonDecoder.string().map(stringDate => new Date(stringDate)),
}, "VisibleStoreWindow");

const visibleStoreDecoder = JsonDecoder.object<VisibleStore>({
  storeId: JsonDecoder.number(),
  name: JsonDecoder.string(),
  href: JsonDecoder.string(),
  disabled: JsonDecoder.boolean(),
  closesAt: JsonDecoder.nullable(JsonDecoder.string().map(stringDate => new Date(stringDate))),
  lastOrdersAt: JsonDecoder.nullable(JsonDecoder.string().map(stringDate => new Date(stringDate))),
  pausedUntil: JsonDecoder.nullable(JsonDecoder.string().map(stringDate => new Date(stringDate))),
  windowsToday: JsonDecoder.array(visibleStoreWindowDecoder, "VisibleStoreWindow[]"),
}, "VisibleStore");

const visibleStoresResponseDecoder = JsonDecoder.object<{ stores: VisibleStore[] }>({
  stores: JsonDecoder.array(visibleStoreDecoder, "VisibleStore[]"),
}, "VisibleStoresResponse");

export { Delivery, DeliveryGroup, DeliveryGroupStatus, Driver, DriverInfo, DropoutReason, WorkStatus, TimeSlot, Dashboard, DispatchTask, DeliveryTrackingStep, DeliveryTracking, DriverTrackingUpdate, IDispatchClient, VisibleStore, VisibleStoreWindow, visibleStoresResponseDecoder };
